import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import type { AssetStyleId } from 'fym-shared/src/music24/assetStyles';
import { TypedMemo } from '../../../../common/types';
import { fullVideoMessages } from '../fullVideoMessages';
import { FrameLyric, FrameSong, frameCaptionStyle } from './assetFrameVars';
import { frameTextureClass, frameThemeClass, frameWordClass } from './frameClasses';
import { frameWords } from './frameShapes';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the frames; each frame uses its own slice of it */
import styles from './AssetFrame.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The word of the sung line that the still shows as sung now (design s2: the fourth word). */
const LIT_WORD = 3;

const LAYOUTS: Partial<Record<AssetStyleId, string | undefined>> = {
  coverGround: styles.lLground,
  coverCard: styles.lLcard,
  typeOnly: styles.lLtype,
  aiFootage: styles.lLai,
};

/** The themes whose lyric block is centred (design s2, `lyr`): Dreamy, Noir and Neon, and AI footage. */
const CENTRED: ReadonlySet<ReleaseThemeId> = new Set(['dreamy', 'dark', 'neon']);

interface LyricFrameProps {
  themeId: ReleaseThemeId;
  style: AssetStyleId;
  /** 9:16 (the lyric moment) instead of 16:9 (the lyric video). */
  tall: boolean;
  song: FrameSong;
  lyric: FrameLyric;
  vars: React.CSSProperties;
  className?: string;
}

/**
 * A still of a lyric video (design s2) or a lyric moment (s4): the sung line in the display type of
 * the theme with the line before (and, on 16:9, the next line) dimmed in the text type. The caption
 * style of the theme lights the words. The style sets the ground: the blurred cover, the cover as a
 * card, the ground of the theme, or a bank clip.
 */
const LyricFrameComponent: React.FC<LyricFrameProps> = ({ themeId, style, tall, song, lyric, vars, className }) => {
  const intl = useIntl();
  const currentLine = lyric.current;
  const words = useMemo(() => frameWords(currentLine, LIT_WORD, frameCaptionStyle(themeId)), [currentLine, themeId]);
  const centred = (CENTRED.has(themeId) || style === 'aiFootage') && style !== 'coverCard';

  return (
    <div
      className={clsx(styles.fv, frameThemeClass(themeId), LAYOUTS[style], tall && styles.fv916, className)}
      style={vars}
      aria-hidden="true"
      data-frame={tall ? 'lyricMoment' : 'lyricVideo'}
      data-style={style}
    >
      <i className={styles.g} />
      {style === 'coverGround' && (
        <>
          <i className={styles.blur} />
          <i className={styles.scrim} />
        </>
      )}
      {style === 'aiFootage' && (
        <>
          <span className={styles.foot}>
            <span className={styles.lab}>{intl.formatMessage(fullVideoMessages.frameBankClip)}</span>
          </span>
          <i className={styles.low} />
        </>
      )}
      {style === 'coverCard' && <i className={styles.cv} />}
      <span className={clsx(styles.lyr, centred && styles.mid)}>
        {lyric.prev !== '' && <span className={styles.ln}>{lyric.prev}</span>}
        <span className={clsx(styles.ln, styles.cur)}>
          {words.map((word) => (
            <span key={word.key} className={frameWordClass(word.state)}>{`${word.text} `}</span>
          ))}
        </span>
        {!tall && lyric.next !== '' && <span className={styles.ln}>{lyric.next}</span>}
      </span>
      <span className={styles.a}>{`${song.artist} · ${song.title}`}</span>
      <i className={clsx(styles.tex, frameTextureClass(themeId))} />
    </div>
  );
};

export const LyricFrame = TypedMemo(LyricFrameComponent);
